import React, { useState, useEffect } from 'react'
import Modal from '@mui/material/Modal'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import SearchIcon from '@material-ui/icons/Search'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@mui/material/Button'
import { makeStyles } from '@material-ui/core/styles'
import { Auth } from 'aws-amplify'
import { isAdmin, isMultipleOrganization, isPlatformAdmin, getUserData } from '../../../Utils/ServerUtil'
import { saveCatalogSharing } from '../../../Utils/SubmitUtil'
import Divider from '@material-ui/core/Divider'
import { SHARE_USER } from '../../../Constants'
import ContentLoader from 'react-content-loader'
import config from '../../../config/runtime'

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        outline: 'none',
        position: 'relative',
        width: 600,
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(2),
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
    },
    searchInput: {
        marginLeft: theme.spacing(1),
        flex: 1,
        backgroundColor: '#ede8f3',
        borderRadius: '50px',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 2),
        marginBottom: theme.spacing(2),
    },
    listContainer: {
        maxHeight: 500,
        overflowY: 'auto',
        height: 600,
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
        borderTop: '0.5px solid #ccc',
    },
    shareCounter: {
        display: 'flex',
        alignItems: 'center',
        color: '#4f2682',
    },
    checkboxStyle: {
        color: '#4f2682 !important',
        backgroundColor: `${theme.palette.environment.light.level0.base} !important`,
    },
    checkedStyle: {
        color: '#4f2682 !important',
    },
    icon: {
        width: 40,
        height: 40,
        marginRight: theme.spacing(1),
    },
    nameContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginLeft: theme.spacing(2),
    },
    emailText: {
        fontSize: '0.8rem',
        display: 'block',
        color: '#acacad',
    },
}))

const ShareModal = ({
    open,
    eval_id,
    evaluation,
    handleClose,
    handlesShowSuccessModal,
    handleLoading,
}) => {
    const classes = useStyles()
    const [searchValue, setSearchValue] = useState('')
    const [names, setNames] = useState([])
    const [selectedEmployees, setSelectedEmployees] = useState([])
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([])
    const [isShareButtonDisabled, setIsShareButtonDisabled] = useState(true)
    const [filteredEmployees, setFilteredEmployees] = useState([])
    const [sharedCatalog, setSharedCatalog] = useState([])
    const [loading, setLoading] = useState(true)
    const [fetchError, setFetchError] = useState(null)

    const shareCount = selectedEmployees.length

    // Fetch employees data when component mounts
    useEffect(() => {
        fetchEmployeesData()
    }, [])

    // Fetch shared catalog data
    useEffect(() => {
        fetchSharedCatalogData()
    }, [evaluation])

    // Filter employees based on search query
    useEffect(() => {
        if (names && names.length > 0) {
            const filtered = names.filter((employee) =>
                employee.name.toLowerCase().includes(searchValue.toLowerCase())
            )
            setFilteredEmployees(filtered)
        }
    }, [searchValue, names])

    // Update share button disabled state
    useEffect(() => {
        setIsShareButtonDisabled(selectedEmployees.length === 0)
    }, [selectedEmployees])

    // Initialize selected employees based on sharedCatalog
    useEffect(() => {
        if (sharedCatalog && sharedCatalog.length > 0) {
            initializeSelectedEmployees()
        }
    }, [sharedCatalog])

    const fetchSharedCatalogData = async () => {
        if (!evaluation?.evaluation?.eval_id) return;

        try {
            const jwtToken = await getAuthToken();
            if (!jwtToken) return;

            const headers = {
                authorization: jwtToken,
                accept: 'application/json',
            }

            // Add organization header if needed
            addOrganizationHeader(headers);

            const options = {
                method: 'GET',
                headers: headers,
            }

            const url = `${config.API_ENDPOINT}/evaluations/${evaluation?.evaluation?.eval_id}`

            const response = await fetch(url, options)
            if (response.ok) {
                const data = await response.json()

                // Update user_id to key in the fetched data
                const updatedData =
                    data?.evaluation?.shared_with_users?.map((emp) => ({
                        ...emp,
                        key: emp.user_id,
                    })) || []

                // Set the updated shared catalog data
                setSharedCatalog(updatedData)

                // Update selectedEmployeeIds with the updated key values
                if (updatedData && updatedData.length > 0) {
                    const sharedEmployeeKeys = updatedData.map(
                        (emp) => emp.key
                    )
                    setSelectedEmployeeIds((prevState) => [
                        ...prevState,
                        ...sharedEmployeeKeys.filter(key => !prevState.includes(key))
                    ])
                }
            } else {
                setFetchError(`Request failed with status: ${response.status}`)
            }
        } catch (error) {
            setFetchError(`Error fetching shared catalog: ${error.message}`)
        } finally {
            setLoading(false)
        }
    }

    const initializeSelectedEmployees = () => {
        const initialSelectedEmployees = sharedCatalog.map((employee) => ({
            ...employee,
            checked: true,
        }))

        setSelectedEmployees(prevState => {
            // Filter out duplicates
            const existingKeys = prevState.map(emp => emp.key);
            const newEmployees = initialSelectedEmployees.filter(
                emp => !existingKeys.includes(emp.key)
            );
            return [...prevState, ...newEmployees];
        });

        setSelectedEmployeeIds(prevState => {
            const newIds = initialSelectedEmployees.map(emp => emp.key);
            return [...new Set([...prevState, ...newIds])];
        });
    }

    const handleSearchChange = (event) => {
        setSearchValue(event.target.value)
    }

    const handleCheckboxChange = (event, employee) => {
        const isChecked = event.target.checked

        if (isChecked) {
            // Add employee if not already selected
            setSelectedEmployees(prevState => {
                const isAlreadySelected = prevState.some(emp => emp.key === employee.key);
                if (!isAlreadySelected) {
                    return [...prevState, { ...employee, checked: true }];
                }
                return prevState;
            });

            setSelectedEmployeeIds(prevState => {
                if (!prevState.includes(employee.key)) {
                    return [...prevState, employee.key];
                }
                return prevState;
            });
        } else {
            // Remove employee
            setSelectedEmployees(prevState =>
                prevState.filter(emp => emp.key !== employee.key)
            );

            setSelectedEmployeeIds(prevState =>
                prevState.filter(id => id !== employee.key)
            );
        }
    }

    const handleShareCatalog = () => {
        handleLoading(true)
        handleShareCatalogToEndpoint()
        handleClose()
    }

    const getAuthToken = async () => {
        try {
            const session = await Auth.currentSession()
            return session.idToken.jwtToken
        } catch (e) {
            try {
                const session = await Auth.currentSession()
                return session.idToken.jwtToken
            } catch (e) {
                console.log('Error refreshing token', e)
                return null
            }
        }
    }

    const addOrganizationHeader = (headers) => {
        if (isAdmin() || isMultipleOrganization()) {
            const organization =
                localStorage['hw.' + window.btoa('organization')]
            if (organization && window.atob(organization)) {
                headers['HW-Organization'] = window.atob(organization) ? window.atob(organization) : 'f376c401-6295-4038-a146-e8b52eef6385';
            }
        }
    }

    // Fetch employees data
    const fetchEmployeesData = async () => {
        // Check if user is platform admin before making the API call
        const userData = getUserData()
        if (!userData || !userData.isPlatformAdmin) {
            console.log('User is not platform admin, skipping all_users fetch')
            setLoading(false)
            return
        }

        try {
            const jwtToken = await getAuthToken();
            if (!jwtToken) {
                setLoading(false);
                setFetchError("Authentication failed");
                return;
            }

            const headers = {
                authorization: jwtToken,
                accept: 'application/json',
            }

            // Add organization header if needed
            addOrganizationHeader(headers);

            const options = {
                method: 'GET',
                headers: headers,
            }

            const url = `${config.API_ENDPOINT_V3}/all_users`

            const response = await fetch(url, options)
            if (response.ok) {
                const employeeData = await response.json()

                // Filter out the primary_contact_id from the employeeData
                const filteredEmployeeData = employeeData.filter(
                    employee => employee.key !== evaluation?.primary_contact_id
                )

                setNames(filteredEmployeeData)
                setFilteredEmployees(filteredEmployeeData)
            } else {
                setFetchError(`API request failed with status: ${response.status}`)
            }
        } catch (error) {
            setFetchError(`Error occurred during API request: ${error.message}`)
        } finally {
            setLoading(false)
        }
    }

    const handleShareCatalogToEndpoint = async () => {
        const employessData = {
            user_keys: selectedEmployeeIds.map(id => `${id}`),
        }

        try {
            await saveCatalogSharing(employessData, eval_id)
            handleLoading(false)
            handlesShowSuccessModal(true)
        } catch (e) {
            console.error('Error sharing catalog:', e)
            handleLoading(false)
            // You might want to add error handling here
        }
    }

    return (
        <Modal
            className={classes.modal}
            open={open}
            onClose={handleClose}
            closeAfterTransition
        >
            <Paper className={classes.paper}>
                <div className={classes.header}></div>
                <div className={classes.searchInput}>
                    <IconButton>
                        <SearchIcon
                            style={{
                                backgroundColor: '#4f2682',
                                borderRadius: '50%',
                                padding: 10,
                                color: 'white',
                                fontSize: 20,
                            }}
                        />
                    </IconButton>
                    <input
                        type="text"
                        placeholder="Search names..."
                        value={searchValue}
                        onChange={handleSearchChange}
                        style={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            backgroundColor: '#ede8f3',
                            fontSize: '15px',
                        }}
                    />
                </div>
                <div className={classes.listContainer}>
                    {loading ? (
                        <ContentLoader
                            viewBox="0 0 400 150"
                            height={400}
                            width={600}
                        >
                            <circle cx="10" cy="20" r="8" />
                            <rect
                                x="25"
                                y="15"
                                rx="5"
                                ry="5"
                                width="220"
                                height="10"
                            />
                            <circle cx="10" cy="50" r="8" />
                            <rect
                                x="25"
                                y="45"
                                rx="5"
                                ry="5"
                                width="220"
                                height="10"
                            />
                            <circle cx="10" cy="80" r="8" />
                            <rect
                                x="25"
                                y="75"
                                rx="5"
                                ry="5"
                                width="220"
                                height="10"
                            />
                            <circle cx="10" cy="110" r="8" />
                            <rect
                                x="25"
                                y="105"
                                rx="5"
                                ry="5"
                                width="220"
                                height="10"
                            />
                        </ContentLoader>
                    ) : fetchError ? (
                        <Typography color="error">{fetchError}</Typography>
                    ) : (
                        <>
                            {/* Display selected employees at the top */}
                            {selectedEmployees.length > 0 && (
                                <>
                                    {selectedEmployees.map((employee) => (
                                        <div
                                            key={employee.key}
                                            className={classes.listItem}
                                        >
                                            <Checkbox
                                                classes={{
                                                    checked: classes.checkedStyle,
                                                }}
                                                className={classes.checkboxStyle}
                                                checked={true}
                                                onChange={(event) =>
                                                    handleCheckboxChange(
                                                        event,
                                                        employee
                                                    )
                                                }
                                            />
                                            <div className={classes.nameContainer}>
                                                <Typography>{employee.name || 'Unknown'}</Typography>
                                                <Typography
                                                    className={classes.emailText}
                                                >
                                                    {employee.email || 'No email'}
                                                </Typography>
                                            </div>
                                        </div>
                                    ))}
                                    <Divider className={classes.divider} />
                                </>
                            )}

                            {/* Display remaining filtered employees */}
                            {filteredEmployees && filteredEmployees.length > 0 ? (
                                filteredEmployees
                                    .filter(
                                        (employee) =>
                                            !selectedEmployeeIds.includes(employee.key)
                                    )
                                    .map((employee) => (
                                        <div
                                            key={employee.key}
                                            className={classes.listItem}
                                        >
                                            <Checkbox
                                                color="secondary"
                                                checked={selectedEmployeeIds.includes(
                                                    employee.key
                                                )}
                                                onChange={(event) =>
                                                    handleCheckboxChange(
                                                        event,
                                                        employee
                                                    )
                                                }
                                            />
                                            <div className={classes.nameContainer}>
                                                <Typography>
                                                    {employee.name || 'Unknown'}
                                                </Typography>
                                                <Typography
                                                    className={classes.emailText}
                                                >
                                                    {employee.email || 'No email'}
                                                </Typography>
                                            </div>
                                        </div>
                                    ))
                            ) : (
                                searchValue && <Typography>No matching employees found</Typography>
                            )}
                        </>
                    )}
                </div>
                <div className={classes.footer}>
                    <div className={classes.shareCounter}>
                        <img
                            src={SHARE_USER}
                            alt="Share With"
                            className={classes.icon}
                        />
                        <div>
                            <div
                                style={{
                                    color: 'grey',
                                    fontSize: '15px',
                                    fontWeight: 'bold',
                                }}
                            >
                                Share with
                            </div>
                            <div
                                style={{ color: 'grey', fontSize: '12px' }}
                            >{`${shareCount} employees selected`}</div>
                        </div>
                    </div>
                    <Button
                        disabled={isShareButtonDisabled}
                        style={{
                            backgroundColor: isShareButtonDisabled
                                ? '#ccc'
                                : '#4f2682',
                            width: '150px',
                            color: 'white',
                        }}
                        onClick={handleShareCatalog}
                        className={
                            isShareButtonDisabled ? classes.disabledButton : ''
                        }
                    >
                        Share Catalog
                    </Button>
                </div>
            </Paper>
        </Modal>
    )
}

export default ShareModal
